import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, LogOut, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleSignIn = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-tight transition-colors ${
      isActive ? "text-white" : "text-[#B7BCC7] hover:text-white"
    }`;

  const initials = (user?.name || user?.email || "?").trim()[0]?.toUpperCase();

  return (
    <div className="sticky top-0 z-40 glass-panel border-b border-[#2A2A2A]/70">
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-logo">
          <span className="relative flex items-center justify-center w-8 h-8">
            <span className="absolute inset-0 rounded-full bg-[#1565FF]/25 blur-md" />
            <svg width="26" height="26" viewBox="0 0 32 32" className="relative">
              <circle cx="16" cy="16" r="4" fill="#1565FF" />
              <circle cx="6" cy="10" r="2.2" fill="#1565FF" opacity="0.7" />
              <circle cx="26" cy="10" r="2.2" fill="#1565FF" opacity="0.7" />
              <circle cx="6" cy="22" r="2.2" fill="#1565FF" opacity="0.5" />
              <circle cx="26" cy="22" r="2.2" fill="#1565FF" opacity="0.5" />
              <line x1="16" y1="16" x2="6" y2="10" stroke="#1565FF" strokeWidth="0.7" opacity="0.6" />
              <line x1="16" y1="16" x2="26" y2="10" stroke="#1565FF" strokeWidth="0.7" opacity="0.6" />
              <line x1="16" y1="16" x2="6" y2="22" stroke="#1565FF" strokeWidth="0.7" opacity="0.4" />
              <line x1="16" y1="16" x2="26" y2="22" stroke="#1565FF" strokeWidth="0.7" opacity="0.4" />
            </svg>
          </span>
          <div className="leading-none">
            <div className="font-sans font-medium tracking-tight text-white text-[15px]">
              PEPTIDE LABS <span className="text-[#1565FF]">N.I.</span>
            </div>
            <div className="label-mono mt-1">Research Grade</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/products" className={linkCls} data-testid="nav-link-products">Products</NavLink>
          <NavLink to="/categories" className={linkCls} data-testid="nav-link-categories">Categories</NavLink>
          <NavLink to="/knowledge" className={linkCls} data-testid="nav-link-knowledge">Knowledge Base</NavLink>
          <NavLink to="/reviews" className={linkCls} data-testid="nav-link-reviews">Reviews</NavLink>
          <NavLink to="/downloads" className={linkCls} data-testid="nav-link-downloads">Downloads</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-link-about">About</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          {/* Auth: signed in avatar OR sign in button */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="w-9 h-9 rounded-full border border-[#2A2A2A] hover:border-[#1565FF] transition-colors bg-[#171717] grid place-items-center overflow-hidden"
                  aria-label="Account menu"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="text-sm text-white">{initials}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-[#0e0e0e] border-[#2A2A2A] text-white"
              >
                <DropdownMenuLabel className="text-[#B7BCC7] text-xs truncate">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#2A2A2A]" />
                <DropdownMenuItem
                  onClick={() => navigate("/account")}
                  data-testid="nav-menu-account"
                  className="cursor-pointer focus:bg-[#171717] focus:text-white"
                >
                  <User className="mr-2 h-4 w-4" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="nav-menu-logout"
                  className="cursor-pointer focus:bg-[#171717] focus:text-white"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={startGoogleSignIn}
              data-testid="nav-signin-btn"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#2A2A2A] hover:border-[#1565FF] text-sm text-white transition-colors"
            >
              Sign in
            </button>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart-link"
            className="relative inline-flex items-center gap-2 px-3.5 h-10 rounded-full border border-[#2A2A2A] hover:border-[#1565FF] transition-colors text-sm"
          >
            <ShoppingCart size={16} />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="min-w-[20px] h-[20px] px-1 grid place-items-center text-[11px] rounded-full bg-[#1565FF] text-white"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#2A2A2A] bg-[#0B0B0B]">
          <div className="px-6 py-4 flex flex-col gap-4">
            <NavLink to="/products" className={linkCls} onClick={() => setOpen(false)}>Products</NavLink>
            <NavLink to="/categories" className={linkCls} onClick={() => setOpen(false)}>Categories</NavLink>
            <NavLink to="/knowledge" className={linkCls} onClick={() => setOpen(false)}>Knowledge Base</NavLink>
            <NavLink to="/reviews" className={linkCls} onClick={() => setOpen(false)}>Reviews</NavLink>
            <NavLink to="/downloads" className={linkCls} onClick={() => setOpen(false)}>Downloads</NavLink>
            <NavLink to="/about" className={linkCls} onClick={() => setOpen(false)}>About</NavLink>
            {!user && (
              <button
                onClick={startGoogleSignIn}
                data-testid="nav-signin-btn-mobile"
                className="btn-ghost w-fit"
              >
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
